const SENSITIVE_QUERY_KEYS = new Set([
  "guesttoken",
  "streamtoken",
  "signature",
  "impersonate_token",
  "payment_intent_client_secret",
  "token",
]);

export function redactCapabilityUrl(value: unknown): unknown {
  if (typeof value !== "string" || value.length === 0) return value;
  const beforeHash = value.split("#", 1)[0] ?? value;
  const queryIndex = beforeHash.indexOf("?");
  if (queryIndex === -1) return beforeHash;
  const prefix = beforeHash.slice(0, queryIndex);
  const safe = beforeHash.slice(queryIndex + 1).split("&").filter((part) => {
    const rawName = part.split("=", 1)[0] ?? "";
    try {
      return !SENSITIVE_QUERY_KEYS.has(decodeURIComponent(rawName).toLowerCase());
    } catch {
      return true;
    }
  });
  return safe.length > 0 ? `${prefix}?${safe.join("&")}` : prefix;
}

function redactTraceData(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  for (const key of ["url", "http.url", "http.request.url", "http.target", "url.full"]) {
    if (key in data) data[key] = redactCapabilityUrl(data[key]);
  }
  if (typeof data["url.query"] === "string") {
    const original = data["url.query"];
    const hasLeadingQuestionMark = original.startsWith("?");
    const rawQuery = hasLeadingQuestionMark ? original.slice(1) : original;
    const redacted = String(redactCapabilityUrl(`/trace?${rawQuery}`));
    const separatorIndex = redacted.indexOf("?");
    const redactedQuery = separatorIndex >= 0
      ? redacted.slice(separatorIndex + 1)
      : "";
    data["url.query"] = hasLeadingQuestionMark && redactedQuery
      ? `?${redactedQuery}`
      : redactedQuery;
  }
}

export function redactBrowserSentryEvent<T extends {
  request?: { url?: string; headers?: Record<string, string | undefined>; data?: unknown; query_string?: unknown; cookies?: unknown };
  breadcrumbs?: Array<{ data?: Record<string, unknown> }>;
  user?: { email?: string; username?: string; [key: string]: unknown };
  transaction?: string;
  contexts?: { trace?: { data?: Record<string, unknown> } };
  spans?: Array<{ description?: string; data?: Record<string, unknown> }>;
}>(event: T): T {
  if (event.user?.email || event.user?.username) {
    event.user = { ...event.user, email: undefined, username: undefined };
  }
  if (event.request?.url) event.request.url = String(redactCapabilityUrl(event.request.url));
  if (event.request) {
    event.request.data = undefined;
    event.request.query_string = undefined;
    event.request.cookies = undefined;
  }
  if (event.request?.headers) {
    for (const key of Object.keys(event.request.headers)) {
      if (["authorization", "cookie", "x-admin-key", "x-motc-guest-token", "x-turnstile-token"].includes(key.toLowerCase())) delete event.request.headers[key];
    }
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (!breadcrumb.data) continue;
    for (const key of ["from", "to", "url"]) {
      if (key in breadcrumb.data) breadcrumb.data[key] = redactCapabilityUrl(breadcrumb.data[key]);
    }
  }
  if (event.transaction) event.transaction = String(redactCapabilityUrl(event.transaction));
  redactTraceData(event.contexts?.trace?.data);
  for (const span of event.spans ?? []) {
    if (span.description) span.description = String(redactCapabilityUrl(span.description));
    redactTraceData(span.data);
  }
  return event;
}
